import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useAppSelector } from "../store";

export interface ISwapDetails {
  total: number;
  isPromotional: boolean;
  partnershipCode: string | null;
  expectedOutput: {
    header: string;
    fee: number;
    value: string | number;
  };
  conversion: string;
}

export interface ITradeInput {
  code: string;
  logoURL: string;
  maxTotal: string;
  price: number;
  value: string;
  units: string;
  approx: string;
}

export type OrderType = "Buy" | "Sell";

export interface TargetOrderStateV2 {
  orderType: OrderType;
  formCompletion: any;
  ctSuccess: boolean;
  tradeAmount?: number;
  filterOption?: "all" | "buy" | "sell";
  filterToken?: string;
  sortOption?: "date" | "targetHighest" | "targetLowest" | "targetClosest" | "none";
  formValid: boolean;
  targetPrice: number;
  viewSummary: boolean;
  targetCurrencyCode: string;
  buyAssetSide: ITradeInput;
  sellAssetSide: ITradeInput;
  percentageDifference: number;
}

const DEFAULT_PRICE = 1;

// Default input values
export const DEFAULT_INPUT: ITradeInput = {
  code: "Select",
  logoURL: "",
  maxTotal: "",
  price: 0,
  value: "",
  units: "",
  approx: "",
};

export const DEFAULT_TARGET_DETAILS: ISwapDetails = {
  total: 0,
  isPromotional: false,
  partnershipCode: null,
  expectedOutput: {
    header: "",
    fee: 0,
    value: "",
  },
  conversion: "",
};

const initialState: TargetOrderStateV2 = {
  buyAssetSide: DEFAULT_INPUT,
  sellAssetSide: DEFAULT_INPUT,
  orderType: "Sell",
  tradeAmount: undefined,
  targetPrice: 0,
  ctSuccess: false,
  viewSummary: false,
  targetCurrencyCode: "",
  formValid: false,
  filterOption: "all",
  filterToken: undefined,
  sortOption: "none",
  formCompletion: {
    tokenValueInput: false,
    currencyValueInput: false,
    tokenInput: false,
    currencyInput: false,
  },
  percentageDifference: 0,
};

export const TargetOrderSliceV2 = createSlice({
  name: "targetsV2",
  initialState: initialState,
  reducers: {
    resetForm: (state, action: PayloadAction<{ userBaseCurrency: string }>) => {
      const userBaseCurrency = action.payload.userBaseCurrency;

      const resetState = {
        ...initialState,
        orderType: state.orderType,

        buyAssetSide: { ...DEFAULT_INPUT },
        sellAssetSide: { ...DEFAULT_INPUT },
      };

      if (resetState.orderType === "Sell") {
        resetState.buyAssetSide.code = userBaseCurrency;
        resetState.buyAssetSide.price = DEFAULT_PRICE;
      } else if (resetState.orderType === "Buy") {
        resetState.sellAssetSide.code = userBaseCurrency;
        resetState.sellAssetSide.price = DEFAULT_PRICE;
      }

      return resetState;
    },
    setBuyAssetSide(state, action: PayloadAction<ITradeInput>) {
      state.buyAssetSide = { ...state.buyAssetSide, ...action.payload };
    },
    setSellAssetSide(state, action: PayloadAction<ITradeInput>) {
      state.sellAssetSide = { ...state.sellAssetSide, ...action.payload };
    },
    setInitialCurrencies(state, action: PayloadAction<ITradeInput>) {
      const userBaseCurrency = action.payload;
      if (state.orderType === "Sell" && state.buyAssetSide.code === "Select") {
        state.buyAssetSide = userBaseCurrency;
        state.buyAssetSide.price = 1;
        state.buyAssetSide.maxTotal = userBaseCurrency.maxTotal;
      } else if (state.orderType === "Buy" && state.sellAssetSide.code === "Select") {
        state.sellAssetSide = userBaseCurrency;
        state.sellAssetSide.price = 1;
        state.sellAssetSide.maxTotal = userBaseCurrency.maxTotal;
      }
    },
    setPercentageDifference(state, action: PayloadAction<number>) {
      state.percentageDifference = action.payload;
    },
    setCtSuccess(state, action: PayloadAction<boolean>) {
      state.ctSuccess = action.payload;
    },
    setOrderType(state, action: PayloadAction<{ orderType: OrderType; userBaseCurrency: string }>) {
      const { orderType, userBaseCurrency } = action.payload;
      state.orderType = orderType;

      if (orderType === "Sell") {
        state.buyAssetSide = {
          ...DEFAULT_INPUT,
          code: userBaseCurrency,
          price: 1,
        };
        state.sellAssetSide = DEFAULT_INPUT;
      } else if (orderType === "Buy") {
        state.sellAssetSide = {
          ...DEFAULT_INPUT,
          code: userBaseCurrency,
          price: 1,
        };
        state.buyAssetSide = DEFAULT_INPUT;
      }
    },
    setTargetTradeAmount(state, action) {
      state.tradeAmount = action.payload;
    },
    toggleSummaryView(state) {
      state.viewSummary = !state.viewSummary;
    },
    toggleValidForm(state) {
      state.formValid = !state.formValid;
    },
    setTargetPrice(state, action) {
      state.targetPrice = action.payload;
    },
    setFilterRedux(state, action) {
      state.filterOption = action.payload;
    },
    setTokenRedux(state, action) {
      state.filterToken = action.payload;
    },
    setSortRedux(state, action) {
      state.sortOption = action.payload;
    },
    setCompletedFormSection(state, action) {
      state.formCompletion = {
        ...state.formCompletion,
        ...{ [action.payload]: true },
      };
    },
    resetFormField(state, action) {
      state.formCompletion = {
        ...state.formCompletion,
        ...{ [action.payload]: false },
      };
    },
  },
});

export const {
  setBuyAssetSide,
  setCtSuccess,
  resetFormField,
  setOrderType,
  setTargetTradeAmount,
  setTargetPrice,
  setPercentageDifference,
  setCompletedFormSection,
  setSellAssetSide,
  setInitialCurrencies,
  toggleSummaryView,
  toggleValidForm,
  resetForm,
  setFilterRedux,
  setTokenRedux,
  setSortRedux,
} = TargetOrderSliceV2.actions;
export const useTargetStateV2 = () => useAppSelector((state) => state.targetsV2);
